import configuration from "@/configuration";
import { PageType } from "@/services/shared-models";
import { useDataStore, useLocaleStore } from "@/stores";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

const Footer = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const pages = useDataStore((state) => state.pages);
    const footerLinks = useDataStore((state) => state.footerLinks);

    const [email, setEmail] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [error, setError] = useState("");

    const { t } = useTranslation(["common"]);

    const { locale } = useParams();

    const handleSubscribe = (event: React.FormEvent<HTMLFormElement>) => {
        setError("");
        setIsSubscribed(false);

        if (!validateEmail(email)) {
            event.preventDefault();

            return;
        }

        setIsSubscribed(true);
    };

    const validateEmail = (value: string) => {
        if (value.trim().length === 0) {
            setError(t("footer.newsletter.invalid"));
            return false;
        } else {
            if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
                setError(t("footer.newsletter.invalid"));
                return false;
            }
        }

        setError("");
        return true;
    };

    const renderMainLinks = () => {
        const links = [
            pages.find((x) => PageType[x.pageType as keyof typeof PageType] === PageType.Step1),
            pages.find((x) => PageType[x.pageType as keyof typeof PageType] === PageType.Step2),
            pages.find((x) => PageType[x.pageType as keyof typeof PageType] === PageType.Step3),
            pages.find((x) => x.pageType === PageType.Elements),
            pages.find((x) => x.pageType === PageType.Download),
        ].filter((x) => x !== undefined);

        return links.map((link) => (
            <Link key={link.id} className="hover:underline cursor-pointer" to={`/${locale}/${link.slug}`}>
                {link.title}
            </Link>
        ));
    };

    const renderMoreLinks = () => {
        const links = pages.filter((x) => x.pageType === PageType.Content && !x.parentPageId);

        return links
            .sort((a, b) => a.parentOrder - b.parentOrder)
            .map((link) => (
                <Link key={link.id} className="hover:underline cursor-pointer" to={`/${locale}/${link.slug}`}>
                    {link.title}
                </Link>
            ));
    };

    const renderFooterLinks = () => {
        return footerLinks
            .filter((x) => !x.targetUrl.startsWith("mailto:") && !x.targetUrl.includes("contact"))
            .map((link) => {
                const target = link.targetUrl.startsWith(window.location.origin) ? "_self" : "_blank";

                return (
                    <a key={link.id} className="hover:underline cursor-pointer" href={link.targetUrl} target={target}>
                        {link.linkText}
                    </a>
                );
            });
    };

    const renderFooterLinksContact = () => {
        return footerLinks
            .filter((x) => x.targetUrl.startsWith("mailto:") || x.targetUrl.includes("contact"))
            .map((link) => {
                return (
                    <a key={link.id} className="hover:underline cursor-pointer" href={link.targetUrl}>
                        {link.linkText}
                    </a>
                );
            });
    };

    return (
        <footer>
            <div className="container mx-auto 2xl:px-[108px] grid grid-cols-4 mt-31 gap-5 font-medium">
                <div className="flex flex-col gap-5">
                    <strong>{t("footer.categories.bimids")}</strong>
                    <div className="flex flex-col gap-5 text-secondary-400">{renderMainLinks()}</div>
                </div>

                <div className="flex flex-col gap-5">
                    <strong>{t("footer.categories.more")}</strong>
                    <div className="flex flex-col gap-5 text-secondary-400">{renderMoreLinks()}</div>
                </div>

                <div className="grid grid-cols-subgrid col-span-2 gap-y-12">
                    <div className="grid col-span-2 gap-3">
                        <strong>{t("footer.newsletter.title")}</strong>
                        <form
                            action="https://bimids.us15.list-manage.com/subscribe/post"
                            method="POST"
                            target="_blank"
                            onSubmit={handleSubscribe}
                            className="flex pt-2"
                        >
                            <input type="hidden" name="u" value="61e7545cb6565a4328e35a12b" />
                            <input type="hidden" name="id" value="26a13d5ab0" />
                            <div className="relative flex flex-auto rounded-l-full bg-white border border-r-0 -outline-offset-1 border-[#E6E8EC] h-12">
                                <input
                                    id="email"
                                    name="MERGE0"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t("footer.newsletter.placeholder")}
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    className="block min-w-0 grow py-1.5 pl-6 pr-6 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none autofill:rounded-l-full"
                                />
                            </div>
                            <button
                                type="submit"
                                className="flex shrink-0 items-center gap-x-1.5 rounded-r-full bg-primary-500 pl-4 pr-5 py-2 text-white hover:bg-primary-600 cursor-pointer font-medium h-12"
                            >
                                {t("footer.newsletter.button")}
                            </button>
                        </form>
                        {!error && !isSubscribed && (
                            <span className="italic text-secondary-400">{t("footer.newsletter.description")}</span>
                        )}
                        {error && <span className="italic text-red-500">{error}</span>}
                        {isSubscribed && (
                            <span className="italic text-primary-500">{t("footer.newsletter.success")}</span>
                        )}
                    </div>

                    <div className="flex flex-col gap-5">
                        <strong>{t("footer.categories.other")}</strong>
                        <div className="flex flex-col gap-5 text-secondary-400">{renderFooterLinks()}</div>
                    </div>

                    <div className="flex flex-col gap-5">
                        <strong>{t("footer.categories.contact")}</strong>
                        <div className="flex flex-col gap-5 text-secondary-400">{renderFooterLinksContact()}</div>
                    </div>
                </div>

                <hr className="h-px col-span-4 bg-secondary-300 border-0 mt-15 mb-5" />

                <div className="flex flex-col gap-5 col-span-4 mb-10">
                    <div className="flex gap-7.5 items-center">
                        <div className="flex items-center shrink-0">
                            <img
                                src={`${configuration.assets}content/logo-bimids.png`}
                                alt="Logo BIMids"
                                className="w-auto h-16"
                            />
                        </div>
                        <span className="text-secondary-400 text-base font-medium">
                            &copy; 2023-{new Date().getFullYear()} BIMids.
                            <br />
                            {t("footer.copyright")}
                        </span>
                        <div className="ml-auto flex gap-20">
                            <a href={t("footer.logo-buildwise")} target="_blank" rel="noopener noreferrer">
                                <img src={`/assets/logo-buildwise.png`} alt="Logo BIMids" className="w-auto h-16" />
                            </a>
                            <a href={t("footer.logo-crtib")} target="_blank" rel="noopener noreferrer">
                                <img src={`/assets/logo-crtib.png`} alt="Logo BIMids" className="w-auto h-16" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto 2xl:px-[108px]">
                <hr className="h-px col-span-4 bg-secondary-300 border-0" />

                {activeRegion === "be" && (
                    <div className="mx-auto max-w-2xl lg:max-w-none my-12">
                        <div className="mx-auto grid grid-cols-3 gap-x-8 gap-y-10 sm:gap-x-10 lg:mx-0 items-center">
                            <h2 className="text-lg/8 font-semibold text-gray-900">{t("footer.partners.message")}</h2>
                            <img
                                alt="AN BIM"
                                src="/assets/partners/partner-AN-BIM.png"
                                width="auto"
                                height={96}
                                className="col-span-2 max-h-24 w-full object-contain object-left lg:col-span-1"
                            />
                            <img
                                alt="FOD Economie"
                                src="/assets/partners/partner-fod-economie.png"
                                width={"auto"}
                                height={96}
                                className="col-span-2 max-h-24 w-full object-contain object-left lg:col-span-1"
                            />
                        </div>
                    </div>
                )}

                {activeRegion === "lu" && (
                    <div className="mx-auto max-w-2xl lg:max-w-none my-12">
                        <div className="mx-auto grid grid-cols-3 gap-x-8 gap-y-10 sm:gap-x-10 lg:mx-0 items-center">
                            <h2 className="text-lg/8 font-semibold text-gray-900 text-center">{t("footer.partners.message")}</h2>
                            <a href="https://mmtp.gouvernement.lu/fr.html" target="_blank">
                                <img
                                    alt="Ministère de la Mobilité et des Travaux publics"
                                    src="/assets/partners/partner-gov-lux.png"
                                    width="auto"
                                    height={96}
                                    className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                                />
                            </a>
                            <a href="https://www.cdm.lu/" target="_blank">
                                <img
                                    alt="Chambre des Métiers"
                                    src="/assets/partners/partner-chambre-metiers-lux.png"
                                    width={"auto"}
                                    height={96}
                                    className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                                />
                            </a>
                            <a href="https://www.oai.lu/fr/1/accueil/" target="_blank">
                                <img
                                    alt="OAI - Ordre des Architectes et des Ingénieurs-Conseils"
                                    src="/assets/partners/partner-OAI-lux.png"
                                    width="auto"
                                    height={96}
                                    className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                                />
                            </a>
                            <a href="https://fda.lu/" target="_blank">
                                <img
                                    alt="Fédération des Artisans"
                                    src="/assets/partners/partner-artisans-lux.png"
                                    width={"auto"}
                                    height={96}
                                    className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                                />
                            </a>
                            <a href="http://groupement.lu/" target="_blank">
                                <img
                                    alt="Groupement des entrepreneurs"
                                    src="/assets/partners/partner-entrepreneurs-lux.png"
                                    width="auto"
                                    height={96}
                                    className="col-span-2 max-h-24 w-full object-contain lg:col-span-1"
                                />
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </footer>
    );
};

export default Footer;
